import { computed, ref, watch, type Ref } from 'vue'
import type { CanvasRetrieve } from '@openapi'
import { omit, isEqual } from 'lodash-es'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types.ts'
import type { InstanceSettings } from '@lib/types/instanceSettings.types.ts'

type Source = 'onUpdateFromEmbeddable' | 'fetchedAccountData'
type ContextKey = string

const SAFE_CONTEXT_KEYS = ['pitcher']

export default (
  pitcherSettings: Ref<OrganizationSettings & InstanceSettings>,
  activeCanvas: Ref<CanvasRetrieve | null>,
) => {
  const temporaryContext = ref<Record<any, any>>({})
  const temporaryContextKeysMap = ref<Record<Source, ContextKey[]>>({} as Record<Source, ContextKey[]>)
  const temporaryContextKeys = computed(() => Array.from(new Set(Object.values(temporaryContextKeysMap.value).flat())))

  const setTemporaryContextKeys = (source: Source, context: CanvasRetrieve['context']) => {
    const sourceKeys = temporaryContextKeysMap.value[source] ?? []
    const objectKeys = Object.keys(context).filter((key) => !SAFE_CONTEXT_KEYS.includes(key))

    temporaryContextKeysMap.value[source] = Array.from(new Set([...sourceKeys, ...objectKeys]))
    objectKeys.forEach((key) => (temporaryContext.value[key] = context?.[key]))
  }

  const clearTemporaryContext = () => {
    temporaryContextKeysMap.value = {} as Record<Source, ContextKey[]>
    temporaryContext.value = {}
  }

  const getSafeContext = (context = activeCanvas.value?.context ?? {}, _temporaryContextKeys?: ContextKey[]) => {
    if (!pitcherSettings.value?.enable_crm_privacy_mode) {
      return context
    }

    let excludedContextKeys = temporaryContextKeys.value

    if (_temporaryContextKeys) {
      excludedContextKeys = _temporaryContextKeys.filter((key) => !SAFE_CONTEXT_KEYS.includes(key))

      if (excludedContextKeys.length) {
        console.log(`Keys excluded from context: ${excludedContextKeys.join(', ')}`, activeCanvas.value?.context)
      }
    } else {
      if (excludedContextKeys.length) {
        console.log(
          `Keys excluded from context: ${excludedContextKeys.join(', ')} based on map:`,
          temporaryContextKeysMap.value,
          activeCanvas.value?.context,
        )
      }
    }

    return omit(context ?? {}, excludedContextKeys)
  }

  const watchCanvas = () =>
    watch([activeCanvas, temporaryContextKeys], ([newCanvas, newContextKeys], [oldCanvas]) => {
      if (!newCanvas || (newCanvas.id !== oldCanvas?.id && oldCanvas?.id)) {
        return
      }

      newContextKeys.forEach((key) => {
        if (key in (newCanvas?.context ?? {}) && !isEqual(temporaryContext.value[key], newCanvas.context[key])) {
          temporaryContext.value[key] = newCanvas.context[key]
        } else {
          if (!newCanvas.context) {
            newCanvas.context = {}
          }

          newCanvas.context[key] = temporaryContext.value[key]
          console.log('Restore temporary context key:', key, newCanvas.context)
        }
      })
    })

  return {
    setTemporaryContextKeys,
    clearTemporaryContext,
    temporaryContextKeys,
    getSafeContext,
    watchCanvas,
  }
}
